import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';

import useFeatureFlagsSwitcher from '../../../../common/hooks/useFeatureFlagsSwitcher';
import { BeneficiarySchema } from '../../components/BeneficiaryForm/beneficiarySchema';
import { ApplicantType, ApplicationType } from '../../types';
import { GetBeneficiariesDataDocument } from './graphql/queries/getBeneficiariesData.gql';
import machineConfig, { model, State } from './machine';
import { Model } from './machine/model';
import BeneficiariesSummary from './pages/BeneficiariesSummary';
import BeneficiariesSummaryLegacy from './pages/BeneficiariesSummaryLegacy';
import ConfirmBeneficiaries from './pages/ConfirmBeneficiaries';
import { ConfirmBeneficiariesFormSchema } from './pages/ConfirmBeneficiaries/confirmBeneficiariesForm.schema';
import ManageBeneficiaries from './pages/ManageBeneficiaries';
import ManageBeneficiary, {
  ManageBeneficiaryViews,
} from './pages/ManageBeneficiary';
import ShareholdersBeneficiaries from './pages/ShareholdersBeneficiaries';
import ShareholdersCompanyOwnership from './pages/ShareholdersCompanyOwnership';

interface BeneficiariesProps {
  companyProfileId: string;
  applicantType: ApplicantType.AccountHolder | ApplicantType.Mandatee;
  applicationType: ApplicationType.Standard | ApplicationType.CapitalDeposit;
  showSkipButton?: boolean;
}

const BeneficiariesComponent = withStateMachine<Model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const { applicantType, applicationType, showSkipButton, viewer } =
      moduleData;
    const stringifiedState = stringifyState<State>(state);
    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    switch (stringifiedState) {
      case State.ENTRY: {
        return (
          <BeneficiariesSummary
            companyName={viewer.company.profile.legalName}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
            onSkip={() => send(model.events.SKIP_BENEFICIARY_LIST())}
            showSkipButton={showSkipButton}
          />
        );
      }
      case State.SEND_BENEFICIARY_INVITATIONS_WITHOUT_BENEFICIARIES:
      case State.BENEFICIARY_LIST: {
        return (
          <ManageBeneficiaries
            data={viewer}
            goBeneficiaryForm={(payload: {
              beneficiaryId: string | null;
              view: ManageBeneficiaryViews;
            }) => {
              send(model.events.GO_MANAGE_BENEFICIARY(payload));
            }}
            isSending={state.matches(
              State.SEND_BENEFICIARY_INVITATIONS_WITHOUT_BENEFICIARIES,
            )}
            onMoralBeneficiaryPress={() =>
              send(model.events.GO_MORAL_BENEFICIARY())
            }
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
            onSkip={() => send(model.events.SKIP_BENEFICIARY_LIST())}
            showSkipButton={showSkipButton}
          />
        );
      }

      case State.DELETE_BENEFICIARY:
      case State.SUBMIT_BENEFICIARY_FORM:
      case State.MANAGE_BENEFICIARY: {
        if (
          state.event.type !== 'GO_MANAGE_BENEFICIARY' &&
          state.event.type !== 'SUBMIT_BENEFICIARY_FORM' &&
          state.event.type !== 'DELETE_BENEFICIARY'
        ) {
          throw new Error(
            `Beneficiary form page accessed with event type: ${state.event.type}`,
          );
        }

        const { beneficiaryId, view } = state.event.data;

        return (
          <ManageBeneficiary
            beneficiaryId={beneficiaryId}
            data={viewer}
            isDeleting={state.matches(State.DELETE_BENEFICIARY)}
            isSending={state.matches(State.SUBMIT_BENEFICIARY_FORM)}
            onDeleteBeneficiary={() => {
              if (!beneficiaryId) {
                throw new Error(
                  `beneficiary ID not passed through in the ${view} ManageBeneficiary view`,
                );
              }

              send(model.events.DELETE_BENEFICIARY({ beneficiaryId, view }));
            }}
            onPrev={onPrev}
            onSubmit={(formValues: BeneficiarySchema) => {
              send(
                model.events.SUBMIT_BENEFICIARY_FORM({
                  beneficiaryId,
                  formValues,
                  view,
                }),
              );
            }}
            view={view}
          />
        );
      }

      case State.INTRO: {
        return (
          <BeneficiariesSummaryLegacy
            applicantType={applicantType}
            applicationType={applicationType}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }
      case State.EDIT:
      case State.SEND_INVITATIONS:
        return (
          <ShareholdersBeneficiaries
            applicantType={applicantType}
            applicationType={applicationType}
            data={viewer}
            isSaving={state.matches(State.SEND_INVITATIONS)}
            onMoralBeneficiaryPress={() =>
              send(model.events.GO_MORAL_BENEFICIARY())
            }
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.SEND_BENEFICIARY_INVITATIONS:
      case State.CONFIRM_BENEFICIARIES: {
        return (
          <ConfirmBeneficiaries
            data={viewer}
            isSaving={state.matches(State.SEND_BENEFICIARY_INVITATIONS)}
            onPrev={onPrev}
            onSubmit={(formValues: ConfirmBeneficiariesFormSchema) => {
              send(
                model.events.MARK_BENEFICIARY_LIST_AS_COMPLETE({
                  formValues,
                }),
              );
            }}
          />
        );
      }

      case State.MORAL_BENEFICIARY:
        return <ShareholdersCompanyOwnership onPrev={onPrev} />;

      case State.INIT:
      case State.ABORT_TO_RESET_PROMPT:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

const Beneficiaries = asBentoModule<BeneficiariesProps>(
  ({
    applicantType,
    applicationType,
    companyProfileId,
    onDone,
    showSkipButton = false,
  }) => {
    const query = useQuery(GetBeneficiariesDataDocument, {
      variables: {
        companyProfileId,
        includeShares: applicationType === ApplicationType.CapitalDeposit,
      },
    });

    const { featureFlags } = useFeatureFlagsSwitcher();

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(moduleData) => (
          <BeneficiariesComponent
            moduleData={{
              ...moduleData,
              applicantType,
              applicationType,
              showNewOnboardingBeneficiaryFlow:
                featureFlags.enableStandardOnboardingBeneficiaryFlowRedesign,
              showSkipButton,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

export default Beneficiaries;
